import React, { useState } from 'react';
import { useAccount, useDisconnect } from 'wagmi';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { Wallet, LogOut, Loader2, AlertCircle } from 'lucide-react';
import { colors, spacing, typography, borders, transitions, shadows } from '../../tokens';
import { formatAddress } from '../../web3/utils/blockchain';
import { useAuth } from '../../web3/hooks/useAuth';

export interface ConnectButtonProps {
  /**
   * Button size
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Show full address or abbreviated
   */
  showFullAddress?: boolean;
  /**
   * Require authentication (SIWE) after connection
   */
  requireAuth?: boolean;
  /**
   * Custom label for connect button
   */
  connectLabel?: string;
  /**
   * Callback after connection
   */
  onConnect?: () => void;
  /**
   * Callback after disconnection
   */
  onDisconnect?: () => void;
}

/**
 * ConnectButton - Web3 Wallet Connection Button with Web3Modal
 *
 * Features:
 * - Connect/Disconnect wallet via Web3Modal
 * - Display wallet address
 * - SIWE authentication integration
 * - Loading and error states
 * - Support for 300+ wallets automatically
 * - Wallet order: Binance Wallet, MetaMask, WalletConnect, Trust Wallet
 *
 * Usage:
 * ```tsx
 * <ConnectButton requireAuth size="md" />
 * ```
 */
export const ConnectButton: React.FC<ConnectButtonProps> = ({
  size = 'md',
  showFullAddress = false,
  requireAuth = false,
  connectLabel = 'Connect Wallet',
  onConnect,
  onDisconnect,
}) => {
  const { address, isConnected, isConnecting } = useAccount();
  const { disconnect } = useDisconnect();
  const { open } = useWeb3Modal();
  const { isAuthenticated, login, logout, isAuthenticating } = useAuth();

  const [isHovered, setIsHovered] = useState(false);

  // Size configurations
  const sizeConfig = {
    sm: {
      padding: `${spacing.sm} ${spacing.md}`,
      fontSize: typography.fontSize.sm,
      iconSize: 16,
    },
    md: {
      padding: `${spacing.md} ${spacing.lg}`,
      fontSize: typography.fontSize.base,
      iconSize: 20,
    },
    lg: {
      padding: `${spacing.lg} ${spacing.xl}`,
      fontSize: typography.fontSize.lg,
      iconSize: 24,
    },
  };

  const config = sizeConfig[size];

  // Base button styles
  const baseStyles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.sm,
    padding: config.padding,
    fontSize: config.fontSize,
    fontWeight: typography.fontWeight.medium,
    borderRadius: borders.component.default.radius,
    border: 'none',
    cursor: 'pointer',
    transition: transitions.default,
    fontFamily: typography.fontFamily.sans,
  };

  // Connect button styles - With glow and glass effects
  const connectStyles: React.CSSProperties = {
    ...baseStyles,
    backgroundColor: colors.brand.primary.base,
    color: colors.neutral.white,
    boxShadow: isHovered
      ? `${shadows.interactive.purpleHover}, ${shadows.elevation.medium}`
      : shadows.interactive.purple,
    transform: isHovered ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
    transition: transitions.button,
    backdropFilter: 'blur(8px)',
  };

  // Connected button styles - With glass effect
  const connectedStyles: React.CSSProperties = {
    ...baseStyles,
    backgroundColor: isHovered
      ? 'rgba(26, 29, 41, 0.8)'  // Semi-transparent for glass effect
      : 'rgba(20, 22, 31, 0.6)',
    backdropFilter: 'blur(12px)',
    color: colors.text.primary.default,
    border: `1px solid ${isHovered
      ? colors.border.emphasis.default
      : colors.border.default.default}`,
    boxShadow: isHovered
      ? shadows.glass.md
      : shadows.glass.sm,
    transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
    transition: transitions.button,
  };

  // Handle connect click - Opens Web3Modal
  const handleConnectClick = async () => {
    try {
      await open({ view: 'Connect' });
      onConnect?.();
    } catch (error) {
      console.error('Failed to open Web3Modal:', error);
    }
  };

  // Handle disconnect
  const handleDisconnect = () => {
    if (requireAuth && isAuthenticated) {
      logout(true); // Logout and disconnect wallet
    } else {
      disconnect();
    }
    onDisconnect?.();
  };

  // Handle authentication
  const handleAuthenticate = async () => {
    try {
      await login();
    } catch (error) {
      // Authentication failed - error is handled by useAuth hook
    }
  };

  // Not connected state
  if (!isConnected) {
    return (
      <button
        style={connectStyles}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleConnectClick}
        disabled={isConnecting}
      >
        {isConnecting ? (
          <Loader2 size={config.iconSize} className="animate-spin" />
        ) : (
          <Wallet size={config.iconSize} />
        )}
        <span>{isConnecting ? 'Connecting...' : connectLabel}</span>
      </button>
    );
  }

  // Connected but not authenticated (if requireAuth is true)
  if (requireAuth && !isAuthenticated) {
    return (
      <div style={{ display: 'flex', gap: spacing.sm }}>
        <button
          style={connectStyles}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleAuthenticate}
          disabled={isAuthenticating}
        >
          {isAuthenticating ? (
            <Loader2 size={config.iconSize} className="animate-spin" />
          ) : (
            <AlertCircle size={config.iconSize} />
          )}
          <span>{isAuthenticating ? 'Signing...' : 'Sign In'}</span>
        </button>
        <button
          style={connectedStyles}
          onClick={handleDisconnect}
        >
          <LogOut size={config.iconSize} />
        </button>
      </div>
    );
  }

  // Connected and authenticated state - Click to open account view
  return (
    <button
      style={connectedStyles}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => open({ view: 'Account' })}
    >
      <Wallet size={config.iconSize} color={colors.brand.primary.base} />
      <span>
        {showFullAddress ? address : formatAddress(address || '')}
      </span>
      <LogOut size={config.iconSize} />
    </button>
  );
};

export default ConnectButton;
